// core
import { useState } from "react";

// ui primitives
import { Button, Card, CircularProgress, Stack, TextField, Typography } from "Components/UI";

// services
import { requestBreakGlassLink } from "Services/BreakGlassAPI";

// components
import LoginLogo from "./LoginLogo";

// styles
import styles from "./BreakGlass.module.css";

// What the page says when an older backend does not report the lifetime.
const DEFAULT_LINK_MINUTES = 15;

/**
 * The link lifetime request-link reports, in whole minutes.
 *
 * It is configuration, the same for every address, so reading it discloses
 * nothing. Stating it from the response rather than hardcoding it keeps the page
 * in step with the email when an environment overrides the lifetime.
 */
function linkMinutes(response) {
    const seconds = Number(response?.link_expires_in);
    return seconds > 0 ? Math.max(1, Math.floor(seconds / 60)) : DEFAULT_LINK_MINUTES;
}

/**
 * "IdP not working?" — request an emergency sign-in link.
 *
 * Reached only from a login page that has already resolved the typed address to
 * a SAML tenant, but this screen still assumes nothing: the address can be
 * edited here, and the endpoint behind it does not care which one arrives.
 */
export default function BreakGlassRequestLink({ initialEmail = "", onCancel }) {
    const [email, setEmail] = useState(initialEmail);
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [waitSeconds, setWaitSeconds] = useState(0);
    const [failed, setFailed] = useState(false);
    const [expiresInMinutes, setExpiresInMinutes] = useState(DEFAULT_LINK_MINUTES);

    async function submit() {
        if (!email || loading) return;

        setLoading(true);
        setWaitSeconds(0);
        setFailed(false);
        const response = await requestBreakGlassLink(email);
        setLoading(false);

        if (response?.httpStatus === 429) {
            setWaitSeconds(response.retry_after || 0);
            return;
        }

        // Only a real success claims an email was sent. The backend answers 200
        // with status:true for every address - an emergency admin, an ordinary
        // user, nobody - so treating "not 429" as success also swallowed 404s,
        // 500s and network failures, and told people to go check an inbox that
        // was never going to receive anything.
        //
        // Reporting a transport failure leaks nothing: it says the endpoint did
        // not answer, not whether the address belongs to an emergency account.
        if (response?.status) {
            setExpiresInMinutes(linkMinutes(response));
            setSent(true);
            return;
        }

        setFailed(true);
    }

    if (sent) {
        return (
            <Card>
                <Stack align="center" gap={3} p={4}>
                    <LoginLogo />
                    <Typography variant="h6">Check your email</Typography>
                    <Typography className={styles.intro}>
                        If the account exists, a sign-in link has been sent. The link
                        expires in {expiresInMinutes}{" "}
                        {expiresInMinutes === 1 ? "minute" : "minutes"} and can be used
                        once.
                    </Typography>
                    <Typography variant="caption" className={styles.intro}>
                        You will also need a code from your authenticator app.
                    </Typography>
                    <button type="button" className={styles.link} onClick={onCancel}>
                        Back to sign in
                    </button>
                </Stack>
            </Card>
        );
    }

    return (
        <Card>
            <Stack gap={3} p={4}>
                <Stack align="center" gap={2}>
                    <LoginLogo />
                    <Typography variant="h6">Emergency access</Typography>
                    <Typography className={styles.intro}>
                        If your identity provider is unavailable, an administrator with
                        emergency access can sign in with an emailed link plus their
                        authenticator app.
                    </Typography>
                </Stack>

                <TextField
                    label="Email"
                    type="email"
                    value={email}
                    autoFocus
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") submit();
                    }}
                />

                {failed ? (
                    <Typography className={styles.error} role="alert">
                        Emergency access is unavailable right now. Contact your
                        administrator.
                    </Typography>
                ) : null}

                {waitSeconds ? (
                    <Typography className={styles.error} role="alert">
                        Too many attempts. Try again in{" "}
                        {Math.ceil(waitSeconds / 60)} minute
                        {Math.ceil(waitSeconds / 60) === 1 ? "" : "s"}.
                    </Typography>
                ) : null}

                <Button
                    fullWidth
                    disabled={!email || loading}
                    onClick={submit}
                    endIcon={loading ? <CircularProgress size={18} /> : null}
                >
                    Send sign-in link
                </Button>

                <button type="button" className={styles.link} onClick={onCancel}>
                    Back to sign in
                </button>
            </Stack>
        </Card>
    );
}
